// cspell:disable

// File size limits (in bytes)
export const FILE_SIZE_LIMITS = {
  TEXT_FILES: 5 * 1024 * 1024, // 5MB for text files (TXT, CSV, JSON)
  IMAGES_AND_PDF: 10 * 1024 * 1024, // 10MB for images and PDF
  MAX_OVERALL: 10 * 1024 * 1024, // 10MB overall maximum
  GENERATED_FILES: 50 * 1024 * 1024, // 50MB for generated CSV/export files
} as const;

// File type configuration with multiple MIME types per category
interface FileTypeConfigInternal {
  extensions: string[];
  maxSize: number;
  convertToText: boolean;
  mimeTypes: string[];
}

const FILE_TYPE_CONFIGS: Record<string, FileTypeConfigInternal> = {
  // Images - universally safe, no script execution risk
  jpeg: {
    extensions: ['.jpg', '.jpeg'],
    maxSize: FILE_SIZE_LIMITS.IMAGES_AND_PDF,
    convertToText: false,
    mimeTypes: ['image/jpeg', 'image/jpg'],
  },
  png: {
    extensions: ['.png'],
    maxSize: FILE_SIZE_LIMITS.IMAGES_AND_PDF,
    convertToText: false,
    mimeTypes: ['image/png'],
  },
  webp: {
    extensions: ['.webp'],
    maxSize: FILE_SIZE_LIMITS.IMAGES_AND_PDF,
    convertToText: false,
    mimeTypes: ['image/webp'],
  },

  // Text files - completely safe, parseable
  'plain-text': {
    extensions: ['.txt'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/plain'],
  },
  csv: {
    extensions: ['.csv'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/csv', 'text/comma-separated-values', 'application/csv'],
  },
  markdown: {
    extensions: ['.md', '.markdown'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/markdown', 'text/x-markdown', 'application/markdown'],
  },
  yaml: {
    extensions: ['.yml', '.yaml'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/yaml', 'application/x-yaml', 'application/yaml'],
  },
  json: {
    extensions: ['.json'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['application/json', 'text/json'],
  },
  xml: {
    extensions: ['.xml'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['application/xml', 'text/xml'],
  },

  // Programming languages - safe text files
  go: {
    extensions: ['.go'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-go', 'text/plain'],
  },
  javascript: {
    extensions: ['.js', '.jsx', '.mjs'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: [
      'text/javascript',
      'text/x-javascript',
      'application/javascript',
      'application/x-javascript',
    ],
  },
  typescript: {
    extensions: ['.ts', '.tsx'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-typescript', 'application/typescript', 'text/plain'],
  },
  python: {
    extensions: ['.py', '.pyw'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-python', 'text/plain'],
  },
  java: {
    extensions: ['.java'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-java', 'text/plain'],
  },
  c: {
    extensions: ['.c', '.h'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-c', 'text/plain'],
  },
  cpp: {
    extensions: ['.cpp', '.cc', '.cxx', '.hpp', '.hxx'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-c++', 'text/x-cpp', 'text/plain'],
  },
  csharp: {
    extensions: ['.cs'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-csharp', 'text/plain'],
  },
  php: {
    extensions: ['.php'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-php', 'application/x-php', 'text/plain'],
  },
  ruby: {
    extensions: ['.rb'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-ruby', 'text/plain'],
  },
  rust: {
    extensions: ['.rs'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-rust', 'text/plain'],
  },
  swift: {
    extensions: ['.swift'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-swift', 'text/plain'],
  },
  kotlin: {
    extensions: ['.kt', '.kts'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-kotlin', 'text/plain'],
  },
  scala: {
    extensions: ['.scala'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-scala', 'text/plain'],
  },
  dart: {
    extensions: ['.dart'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-dart', 'text/plain'],
  },
  shell: {
    extensions: ['.sh', '.bash', '.zsh', '.fish'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-shell', 'text/x-sh', 'application/x-sh', 'text/plain'],
  },
  sql: {
    extensions: ['.sql'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-sql', 'application/sql', 'text/plain'],
  },
  html: {
    extensions: ['.html', '.htm'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/html', 'text/x-html', 'application/xhtml+xml'],
  },
  css: {
    extensions: ['.css'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/css', 'text/x-css'],
  },
  config: {
    extensions: ['.conf', '.config', '.ini', '.toml'],
    maxSize: FILE_SIZE_LIMITS.TEXT_FILES,
    convertToText: true,
    mimeTypes: ['text/x-config', 'text/plain', 'application/x-config'],
  },

  // PDF - safe for viewing, widely needed
  pdf: {
    extensions: ['.pdf'],
    maxSize: FILE_SIZE_LIMITS.IMAGES_AND_PDF,
    convertToText: false,
    mimeTypes: ['application/pdf'],
  },
};

// Generate the flat SAFE_FILE_TYPES structure for backward compatibility
export const SAFE_FILE_TYPES: Record<
  string,
  { extensions: string[]; maxSize: number; convertToText: boolean }
> = Object.fromEntries(
  Object.values(FILE_TYPE_CONFIGS).flatMap((config) =>
    config.mimeTypes.map((mimeType) => [
      mimeType,
      {
        extensions: config.extensions,
        maxSize: config.maxSize,
        convertToText: config.convertToText,
      },
    ]),
  ),
);

// Helper functions for backward compatibility and convenience
export const ALLOWED_MIME_TYPES = Object.keys(SAFE_FILE_TYPES);

// Create ACCEPT_TYPES with both extensions and MIME types for better browser compatibility
export const ACCEPT_TYPES = [
  ...Object.values(FILE_TYPE_CONFIGS).flatMap((type) => type.extensions),
  ...Object.values(FILE_TYPE_CONFIGS).flatMap((type) => type.mimeTypes),
].join(',');

// Helper function to get file type info
export const getFileTypeInfo = (mimeType: string) => {
  return SAFE_FILE_TYPES[mimeType] || null;
};

// Helper function to check if a file type is supported
export const isSupportedFileType = (mimeType: string): boolean => {
  return mimeType in SAFE_FILE_TYPES;
};

// Helper function to get max size for a file type
export const getMaxFileSize = (mimeType: string): number => {
  return SAFE_FILE_TYPES[mimeType]?.maxSize || FILE_SIZE_LIMITS.TEXT_FILES;
};

// Helper function to check if a file type should be converted to text
export const shouldConvertToText = (mimeType: string): boolean => {
  return SAFE_FILE_TYPES[mimeType]?.convertToText || false;
};

// Helper function to get all supported extensions
export const getAllSupportedExtensions = (): string[] => {
  return Object.values(FILE_TYPE_CONFIGS).flatMap((type) => type.extensions);
};

// Helper function to get MIME types by category
export const getImageMimeTypes = (): string[] => {
  return Object.keys(SAFE_FILE_TYPES).filter((type) =>
    type.startsWith('image/'),
  );
};

export const getTextMimeTypes = (): string[] => {
  return Object.keys(SAFE_FILE_TYPES).filter(
    (type) =>
      type.startsWith('text/') ||
      type.startsWith('application/json') ||
      type.startsWith('application/pdf') ||
      type.startsWith('application/xml') ||
      type.startsWith('application/x-yaml') ||
      type.startsWith('application/yaml') ||
      type.startsWith('application/markdown') ||
      type.startsWith('application/csv') ||
      type.startsWith('application/javascript') ||
      type.startsWith('application/typescript') ||
      type.startsWith('application/x-php') ||
      type.startsWith('application/sql') ||
      type.startsWith('application/xhtml+xml') ||
      type.startsWith('application/x-config'),
  );
};
